import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import * as LucideIcons from 'lucide-react';
import { X, CheckCircle, XCircle, ChevronDown, ChevronRight, Save, Shield, Search } from 'lucide-react';
import clsx from 'clsx';
import { apiService } from '../../../../components/api/apiService';
import { useAccessTheme } from '../AccessManagement/useAccessTheme';

export interface RoutePrivilege {
    id: string;
    uid: string;
    label: string;
    path: string | null;
    icon: string;
    hasAccess: boolean;
    children?: RoutePrivilege[];
}

interface Props {
    roleName: string;
    roleId?: string;
    userId?: string;
    tenantId: string;
    routes: RoutePrivilege[];
    firstPageId?: string | null;
    onClose: () => void;
}

const ViewRoutePrivilegesModal: React.FC<Props> = ({
    roleName,
    roleId,
    userId,
    tenantId,
    routes,
    firstPageId,
    onClose
}) => {
    const t = useAccessTheme();
    const [editedRoutes, setEditedRoutes] = useState<RoutePrivilege[]>(routes);
    const [firstPage, setFirstPage] = useState<string>(firstPageId || '');
    const [selectedRouteId, setSelectedRouteId] = useState<string | null>(null);
    const [toastMessage, setToastMessage] = useState<string | null>(null);
    const [saving, setSaving] = useState(false);

    const toggleAccess = (target: RoutePrivilege, nodes = editedRoutes): RoutePrivilege[] => {
        return nodes.map((r) => {
            if (r.uid === target.uid) return { ...r, hasAccess: !r.hasAccess };
            if (r.children) return { ...r, children: toggleAccess(target, r.children) };
            return r;
        });
    };

    const handleToggle = (route: RoutePrivilege) => {
        setEditedRoutes(toggleAccess(route));
    };

    const allSelected = areAllGranted(editedRoutes);
    const handleToggleAll = () => {
        setEditedRoutes(setAllAccess(editedRoutes, !allSelected));
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            if (userId) {
                await apiService.post('/api/RoutePermission/user/upsert', {
                    tenantId,
                    userId,
                    routes: editedRoutes,
                    firstPageId: firstPage || null
                }, 'backend');
            } else {
                await apiService.post('/api/RoutePermission/upsert', {
                    tenantId,
                    roleId,
                    routes: editedRoutes,
                    firstPageId: firstPage || null
                }, 'backend');
            }
            setToastMessage('Permissions saved successfully.');
        } catch (error) {
            console.error('Save failed:', error);
            setToastMessage('Failed to save permissions.');
        } finally {
            setSaving(false);
            setTimeout(() => setToastMessage(null), 2000);
        }
    };

    const selectedRoute = findRoute(editedRoutes, selectedRouteId);

    return createPortal(
        <div className={`fixed inset-0 z-[100] flex items-center justify-center p-4 ${t.overlay}`}>
            <div className={`rounded-xl shadow-xl w-full max-w-5xl max-h-[92vh] overflow-hidden border relative ${t.panel}`}>
                <div className={`flex items-center justify-between px-5 py-4 border-b ${t.header}`}>
                    <div>
                        <h2 className={`text-lg font-semibold ${t.title}`}>
                            Route Privileges — <span className={t.accentText}>{roleName}</span>
                        </h2>
                        <p className={`text-xs mt-0.5 ${t.muted}`}>
                            {userId
                                ? 'Configure extra feature access for this user. User grants are added on top of the role.'
                                : 'Configure feature access for this role.'}
                        </p>
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            type="button"
                            onClick={handleSave}
                            disabled={saving}
                            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-50 ${t.btnPrimary}`}
                        >
                            <Save className="w-4 h-4" />
                            {saving ? 'Saving...' : 'Save'}
                        </button>
                        <button type="button" onClick={onClose} className={`p-1.5 rounded-lg ${t.closeBtn}`}>
                            <X className="w-5 h-5" />
                        </button>
                    </div>
                </div>

                {toastMessage && (
                    <div className={`absolute top-4 right-6 z-50 text-sm px-3 py-2 rounded-lg border ${t.isLight ? 'bg-white border-neutral-200 shadow' : 'bg-neutral-800 border-neutral-700'}`}>
                        {toastMessage}
                    </div>
                )}

                <div className="flex max-h-[calc(92vh-80px)] overflow-hidden">
                    <div className={`w-full md:w-2/3 overflow-y-auto p-5 border-r ${t.border}`}>
                        <div className="flex flex-col gap-3 mb-3">
                            <FirstPageSelect
                                routes={editedRoutes}
                                value={firstPage}
                                onChange={setFirstPage}
                                t={t}
                            />
                            <div className="flex items-center justify-between">
                                <p className={`text-xs ${t.muted}`}>
                                    {allSelected ? 'All routes granted' : 'Choose routes or grant them all at once'}
                                </p>
                                <button
                                    type="button"
                                    onClick={handleToggleAll}
                                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium ${t.btnSecondary}`}
                                >
                                    {allSelected ? (
                                        <>
                                            <XCircle className="w-3.5 h-3.5" />
                                            Unselect all
                                        </>
                                    ) : (
                                        <>
                                            <CheckCircle className="w-3.5 h-3.5" />
                                            Select all
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                        <RouteTree
                            routes={editedRoutes}
                            onToggle={handleToggle}
                            selected={selectedRouteId}
                            onSelect={setSelectedRouteId}
                            t={t}
                        />
                    </div>

                    <div className={`hidden md:block w-1/3 p-5 ${t.isLight ? 'bg-neutral-50' : 'bg-neutral-950/40'}`}>
                        {selectedRoute ? (
                            <div className="space-y-3">
                                <h4 className={`font-semibold border-b pb-2 ${t.title} ${t.border}`}>Route Details</h4>
                                <div>
                                    <p className={`text-xs ${t.muted}`}>UID</p>
                                    <p className={`text-sm font-mono mt-0.5 ${t.body}`}>{selectedRoute.uid}</p>
                                </div>
                                <div>
                                    <p className={`text-xs ${t.muted}`}>Label</p>
                                    <p className={`text-sm mt-0.5 ${t.title}`}>{selectedRoute.label}</p>
                                </div>
                                <div>
                                    <p className={`text-xs ${t.muted}`}>Path</p>
                                    <p className={`text-sm mt-0.5 font-mono ${t.muted}`}>{selectedRoute.path || '—'}</p>
                                </div>
                                <div className="flex items-center gap-2 pt-1">
                                    <span className={`text-xs ${t.muted}`}>Access:</span>
                                    {selectedRoute.hasAccess ? (
                                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs border ${t.badgeAccent}`}>
                                            <CheckCircle className="w-3 h-3" /> Granted
                                        </span>
                                    ) : (
                                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs border ${t.isLight ? 'bg-red-50 text-red-600 border-red-200' : 'bg-red-900/30 text-red-400 border-red-800/50'}`}>
                                            <XCircle className="w-3 h-3" /> Denied
                                        </span>
                                    )}
                                </div>
                            </div>
                        ) : (
                            <div className="h-full flex flex-col items-center justify-center text-center opacity-60">
                                <Shield className={`w-10 h-10 mb-2 ${t.muted}`} />
                                <p className={`text-xs ${t.muted}`}>Select a route to view details.</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>,
        document.body
    );
};

const FirstPageSelect: React.FC<{
    routes: RoutePrivilege[];
    value: string;
    onChange: (id: string) => void;
    t: ReturnType<typeof useAccessTheme>;
}> = ({ routes, value, onChange, t }) => {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState('');
    const boxRef = useRef<HTMLDivElement>(null);
    const options = useMemo(() => flattenRoutedPages(routes), [routes]);
    const selected = options.find((option) => option.id === value);
    const filtered = options.filter((option) => {
        const haystack = `${option.label} ${option.path}`.toLowerCase();
        return haystack.includes(query.trim().toLowerCase());
    });

    useEffect(() => {
        const onDocClick = (event: MouseEvent) => {
            if (!boxRef.current?.contains(event.target as Node)) setOpen(false);
        };
        document.addEventListener('mousedown', onDocClick);
        return () => document.removeEventListener('mousedown', onDocClick);
    }, []);

    return (
        <div ref={boxRef} className="relative">
            <label className={`block text-xs font-medium mb-1 ${t.muted}`}>First page</label>
            <button
                type="button"
                onClick={() => setOpen((prev) => !prev)}
                className={`w-full flex items-center justify-between gap-2 px-3 py-2 rounded-lg text-sm border ${t.input}`}
            >
                <span className={`truncate text-left ${selected ? t.title : t.muted}`}>
                    {selected ? `${selected.label} (${selected.path})` : 'Use default landing page'}
                </span>
                <ChevronDown className={`w-4 h-4 shrink-0 ${t.muted}`} />
            </button>
            {open && (
                <div className={`absolute z-20 mt-1 w-full rounded-lg border shadow-lg ${t.panel}`}>
                    <div className={`flex items-center gap-2 px-3 py-2 border-b ${t.border}`}>
                        <Search className={`w-3.5 h-3.5 ${t.muted}`} />
                        <input
                            autoFocus
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            placeholder="Filter routes..."
                            className={`w-full bg-transparent text-sm outline-none ${t.body}`}
                        />
                    </div>
                    <ul className="max-h-56 overflow-y-auto py-1">
                        <li>
                            <button
                                type="button"
                                onClick={() => { onChange(''); setOpen(false); setQuery(''); }}
                                className={`w-full text-left px-3 py-2 text-sm ${t.muted} ${t.rowHover}`}
                            >
                                Use default landing page
                            </button>
                        </li>
                        {filtered.map((option) => (
                            <li key={option.id}>
                                <button
                                    type="button"
                                    onClick={() => { onChange(option.id); setOpen(false); setQuery(''); }}
                                    className={clsx(
                                        'w-full text-left px-3 py-2 text-sm',
                                        option.id === value ? t.accentText : t.title,
                                        t.rowHover
                                    )}
                                >
                                    <span className="block truncate">{option.label}</span>
                                    <span className={`block text-xs truncate ${t.muted}`}>{option.path}</span>
                                </button>
                            </li>
                        ))}
                        {filtered.length === 0 && (
                            <li className={`px-3 py-2 text-xs ${t.muted}`}>No routes match.</li>
                        )}
                    </ul>
                </div>
            )}
        </div>
    );
};

function flattenRoutedPages(nodes: RoutePrivilege[], trail: string[] = []): Array<{ id: string; label: string; path: string }> {
    const pages: Array<{ id: string; label: string; path: string }> = [];
    for (const node of nodes) {
        const label = [...trail, node.label].join(' / ');
        if (node.path) pages.push({ id: node.id, label, path: node.path });
        if (node.children?.length) pages.push(...flattenRoutedPages(node.children, [...trail, node.label]));
    }
    return pages;
}

const RouteTree: React.FC<{
    routes: RoutePrivilege[];
    onToggle: (route: RoutePrivilege) => void;
    selected: string | null;
    onSelect: (uid: string) => void;
    t: ReturnType<typeof useAccessTheme>;
}> = ({ routes, onToggle, selected, onSelect, t }) => {
    const [expanded, setExpanded] = useState<{ [uid: string]: boolean }>({});

    return (
        <ul className="space-y-1">
            {routes.map((route) => {
                const isExpanded = expanded[route.uid];
                const IconComponent = (LucideIcons as any)[route.icon] || LucideIcons.SquareDot;
                const isSelected = selected === route.uid;

                return (
                    <li key={route.uid}>
                        <div
                            className={clsx(
                                'flex items-center justify-between rounded-lg px-3 py-2 cursor-pointer border transition-colors',
                                isSelected
                                    ? t.isLight
                                        ? 'bg-indigo-50 border-indigo-200'
                                        : 'bg-indigo-900/20 border-indigo-500/40'
                                    : `border-transparent ${t.rowHover}`
                            )}
                            onClick={() => onSelect(route.uid)}
                        >
                            <div className="flex items-center gap-2 min-w-0">
                                {route.children && route.children.length > 0 ? (
                                    <button
                                        type="button"
                                        className={t.muted}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setExpanded((prev) => ({ ...prev, [route.uid]: !isExpanded }));
                                        }}
                                    >
                                        {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                                    </button>
                                ) : (
                                    <span className="w-4 h-4" />
                                )}
                                <IconComponent className={`w-4 h-4 shrink-0 ${t.accentText}`} />
                                <span className={`text-sm font-medium truncate ${t.title}`}>{route.label}</span>
                            </div>
                            <button
                                type="button"
                                className={clsx(
                                    'flex items-center justify-center w-7 h-7 rounded-full',
                                    route.hasAccess
                                        ? 'text-green-600 hover:bg-green-500/10'
                                        : 'text-red-500 hover:bg-red-500/10'
                                )}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onToggle(route);
                                }}
                            >
                                {route.hasAccess ? <CheckCircle className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
                            </button>
                        </div>
                        {route.children && isExpanded && (
                            <div className="ml-4 mt-1">
                                <RouteTree
                                    routes={route.children}
                                    onToggle={onToggle}
                                    selected={selected}
                                    onSelect={onSelect}
                                    t={t}
                                />
                            </div>
                        )}
                    </li>
                );
            })}
        </ul>
    );
};

function setAllAccess(nodes: RoutePrivilege[], hasAccess: boolean): RoutePrivilege[] {
    return nodes.map((node) => ({
        ...node,
        hasAccess,
        children: node.children?.length ? setAllAccess(node.children, hasAccess) : node.children,
    }));
}

function areAllGranted(nodes: RoutePrivilege[]): boolean {
    return nodes.length > 0 && nodes.every(
        (node) => node.hasAccess && (!node.children?.length || areAllGranted(node.children))
    );
}

function findRoute(routes: RoutePrivilege[], uid: string | null): RoutePrivilege | undefined {
    if (!uid) return undefined;
    for (const r of routes) {
        if (r.uid === uid) return r;
        if (r.children) {
            const found = findRoute(r.children, uid);
            if (found) return found;
        }
    }
    return undefined;
}

export default ViewRoutePrivilegesModal;
